export function offsetFromRoundTrip(input) {
    if (!Number.isFinite(input.serverTimeMs) || !Number.isFinite(input.rttMs) || input.rttMs < 0) {
        return null;
    }
    const midpointMs = input.receivedAtMs - input.rttMs / 2;
    return { offsetMs: input.serverTimeMs - midpointMs, rttMs: input.rttMs };
}
/**
 * Standard four-timestamp (NTP) offset: the server reports when it received the request
 * and when it sent the response, so its own handling time drops out. Only an uneven
 * split of the network delay remains as error (at most half the network delay).
 */
export function offsetFromFourTimestamps(input) {
    const { clientSentMs, serverReceivedMs, serverSentMs, clientReceivedMs } = input;
    if (![clientSentMs, serverReceivedMs, serverSentMs, clientReceivedMs].every(Number.isFinite))
        return null;
    const serverHoldMs = Math.max(0, serverSentMs - serverReceivedMs);
    const networkMs = clientReceivedMs - clientSentMs - serverHoldMs;
    if (networkMs < 0)
        return null;
    const offsetMs = (serverReceivedMs - clientSentMs + (serverSentMs - clientReceivedMs)) / 2;
    return { offsetMs, rttMs: networkMs };
}
/** How far the offset in use moves towards a newer, better sample per update (no jumps). */
export const CLOCK_SMOOTHING = 0.25;
/** Monotonic milliseconds: immune to system clock changes mid-request. */
export function monotonicNowMs() {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
}
export class ServerClock {
    maxSamples;
    localNowMs;
    samples = [];
    smoothedOffsetMs = null;
    constructor(maxSamples = 8, localNowMs = () => Date.now()) {
        this.maxSamples = maxSamples;
        this.localNowMs = localNowMs;
    }
    /**
     * Add a sample. The offset in use moves CLOCK_SMOOTHING of the way towards the fastest
     * recent sample, so the shared timeline never jumps. `snap` takes it straight away
     * (first sample, or the burst when a wall starts).
     */
    record(sample, options = {}) {
        if (!sample)
            return;
        this.samples.push(sample);
        if (this.samples.length > this.maxSamples)
            this.samples.shift();
        const best = this.bestOffsetMs();
        if (best === null)
            return;
        if (options.snap || this.smoothedOffsetMs === null) {
            this.smoothedOffsetMs = best;
        }
        else {
            this.smoothedOffsetMs += (best - this.smoothedOffsetMs) * CLOCK_SMOOTHING;
        }
    }
    /** The offset in use (smoothed), or null before the first sample. */
    offsetMs() {
        return this.smoothedOffsetMs;
    }
    /** Offset of the recent sample with the smallest network delay (most trustworthy). */
    bestOffsetMs() {
        let best = null;
        for (const sample of this.samples) {
            if (!best || sample.rttMs < best.rttMs)
                best = sample;
        }
        return best ? best.offsetMs : null;
    }
    /** Best estimate of the server's current time, in epoch milliseconds. */
    now() {
        return this.localNowMs() + (this.offsetMs() ?? 0);
    }
}
//# sourceMappingURL=server-clock.js.map